import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { BackupAlertService } from './backup-alert.service';

export interface RecoveryInterval {
  timeline: number;
  windowStart: Date;
  windowEnd: Date;
  anchorBackupId: string | null;
  segmentCount: number;
  isCurrent: boolean;
  continuity: 'continuous' | 'gap_before' | 'truncated';
}

/**
 * Computes what instants are actually restorable, and finds the gaps.
 *
 * WHY THIS IS THE MOST IMPORTANT SERVICE IN THE SUBSYSTEM
 *
 * Every other check answers "did the last thing succeed?". This one answers
 * "is the window we advertise real?" — and those come apart silently. A single
 * WAL segment that never uploaded splits the recoverable range in two: you can
 * restore to any instant before it, and any instant after the next base backup,
 * but nothing in between. Every backup still reports success. `pgbackrest info`
 * still prints a plausible archive range. Nothing surfaces the hole until
 * somebody tries to recover into it, which by definition is the worst possible
 * moment to find out.
 *
 * Gap detection is therefore not a nice-to-have on top of backup monitoring.
 * It is the only mechanism in the entire design that can catch this class of
 * failure, and a gap is a P1 the moment it appears.
 *
 * THE OUTPUT IS ALSO THE UI CONTRACT
 *
 * The restore wizard only permits instants that fall inside one of these
 * intervals. You cannot request a restore to a moment the system cannot serve,
 * because the picker will not offer it — which is a better guarantee than
 * validating the request afterwards and failing an operator mid-incident.
 */
@Injectable()
export class RecoveryWindowService {
  private readonly logger = new Logger(RecoveryWindowService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly alerts: BackupAlertService
  ) {}

  @Cron('*/10 * * * *')
  async recomputeAll(): Promise<void> {
    // The whole body is guarded, not just the per-target work. Before migration
    // 057 is applied this table does not exist, and an unguarded scheduled job
    // throws a full Prisma stack trace into the log every ten minutes — which
    // trains people to ignore this service's output, which is the opposite of
    // what a gap detector needs.
    let targets: Array<{ id: string; code: string }>;
    try {
      targets = await this.prisma.backupTarget.findMany({
        where: { isActive: true },
        select: { id: true, code: true },
      });
    } catch (err) {
      this.logger.warn(`recovery window recompute skipped: ${(err as Error).message}`);
      return;
    }

    for (const target of targets) {
      await this.recompute(target.id).catch((err) =>
        this.logger.warn(`recovery window recompute failed for ${target.code}: ${err.message}`)
      );
    }
  }

  /**
   * Rebuild the intervals for one target.
   *
   * Rebuilt wholesale rather than patched incrementally: the computation is
   * cheap at this data volume, and an incrementally-maintained view of "what
   * can we recover" that drifts from reality is precisely the thing this
   * service exists to prevent.
   */
  async recompute(targetId: string): Promise<RecoveryInterval[]> {
    const segments = await this.prisma.walArchiveSegment.findMany({
      where: { targetId, status: 'uploaded' },
      orderBy: [{ timeline: 'asc' }, { segmentName: 'asc' }],
      select: { segmentName: true, timeline: true, uploadedAt: true, archivedAt: true },
    });

    const backups = await this.prisma.backupRun.findMany({
      where: { targetId, status: 'completed', backupType: { in: ['full', 'diff', 'incr'] } },
      orderBy: { startedAt: 'asc' },
      select: { id: true, backupType: true, finishedAt: true, startedAt: true, timeline: true },
    });

    const intervals: RecoveryInterval[] = [];

    // Group by timeline: a PITR cannot replay across a timeline switch without
    // an explicit target, so each timeline's segments form their own runs.
    const byTimeline = new Map<number, typeof segments>();
    for (const segment of segments) {
      const list = byTimeline.get(segment.timeline) ?? [];
      list.push(segment);
      byTimeline.set(segment.timeline, list);
    }

    for (const [timeline, list] of byTimeline) {
      // A base backup opens a window: WAL alone is not restorable, it has to be
      // replayed onto something. The earliest instant we can serve on this
      // timeline is the stop time of its first completed base backup.
      const anchor = backups.find((b) => (b.timeline ?? 1) === timeline && b.backupType === 'full');
      if (!anchor) continue;

      let runStart = 0;

      for (let i = 1; i <= list.length; i++) {
        const previous = list[i - 1];
        const current = list[i];

        const contiguous = current ? this.isNext(previous.segmentName, current.segmentName) : false;
        if (contiguous) continue;

        // Either a gap, or the end of the list — close the run either way.
        const first = list[runStart];
        const last = previous;

        const windowStart = this.instantOf(first, anchor.finishedAt ?? anchor.startedAt);
        const windowEnd = last.uploadedAt ?? last.archivedAt ?? windowStart;

        intervals.push({
          timeline,
          windowStart,
          windowEnd,
          anchorBackupId: anchor.id,
          segmentCount: i - runStart,
          isCurrent: !current,
          // The FIRST run on a timeline is continuous; any later run exists
          // only because something before it is missing.
          continuity: runStart === 0 ? 'continuous' : 'gap_before',
        });

        runStart = i;
      }
    }

    await this.persist(targetId, intervals);
    await this.evaluateGaps(targetId, intervals);

    return intervals;
  }

  /**
   * Is `b` the immediate successor of `a` in the WAL sequence?
   *
   * Segment names are TTTTTTTTLLLLLLLLSSSSSSSS: 8 hex timeline, 8 hex logical
   * file, 8 hex segment. Within a logical file the segment counter runs 00..FE
   * (0xFF is skipped by Postgres), then the logical file increments and the
   * segment counter restarts at 00. Getting this wrong in either direction is
   * costly: too strict invents phantom gaps and pages people at 3am, too loose
   * misses a real one and voids the recovery window silently.
   */
  private isNext(a: string, b: string): boolean {
    if (a.length !== 24 || b.length !== 24) return false;
    if (a.slice(0, 8) !== b.slice(0, 8)) return false;

    const logA = parseInt(a.slice(8, 16), 16);
    const segA = parseInt(a.slice(16, 24), 16);
    const logB = parseInt(b.slice(8, 16), 16);
    const segB = parseInt(b.slice(16, 24), 16);

    // Same logical file, next segment.
    if (logB === logA && segB === segA + 1) return true;
    // Rolled over into the next logical file. 0xFE is the last segment Postgres
    // uses; 0xFF is skipped.
    if (logB === logA + 1 && segA === 0xfe && segB === 0x00) return true;

    return false;
  }

  private instantOf(
    segment: { uploadedAt: Date | null; archivedAt: Date | null },
    fallback: Date
  ): Date {
    return segment.archivedAt ?? segment.uploadedAt ?? fallback;
  }

  private async persist(targetId: string, intervals: RecoveryInterval[]): Promise<void> {
    await this.prisma.$transaction([
      this.prisma.recoveryPoint.deleteMany({ where: { targetId } }),
      ...intervals.map((interval) =>
        this.prisma.recoveryPoint.create({
          data: {
            targetId,
            timeline: interval.timeline,
            windowStart: interval.windowStart,
            windowEnd: interval.windowEnd,
            anchorBackupId: interval.anchorBackupId,
            segmentCount: interval.segmentCount,
            isCurrent: interval.isCurrent,
            continuity: interval.continuity,
          },
        })
      ),
    ]);
  }

  private async evaluateGaps(targetId: string, intervals: RecoveryInterval[]): Promise<void> {
    const gaps = intervals.filter((i) => i.continuity === 'gap_before');

    if (!gaps.length) {
      await this.alerts.resolve('wal_gap', targetId);
      return;
    }

    await this.alerts.raise({
      kind: 'wal_gap',
      severity: 'critical',
      targetId,
      title: `${gaps.length} gap(s) in the recovery window — PITR is broken for those periods`,
      detail: {
        gaps: gaps.map((g) => ({
          timeline: g.timeline,
          unrecoverableBefore: g.windowStart.toISOString(),
        })),
        note:
          'A gap means one or more WAL segments never reached the repository. Every backup ' +
          'still reports success; only this check sees it. Restores into these periods will fail.',
      },
    });
  }

  /**
   * The advertised window, and whether an instant is actually inside it.
   *
   * The UI uses this to constrain its picker rather than validating after the
   * fact — an operator should not be able to select a moment we cannot serve.
   */
  async contains(targetId: string, instant: Date): Promise<boolean> {
    const hit = await this.prisma.recoveryPoint.findFirst({
      where: { targetId, windowStart: { lte: instant }, windowEnd: { gte: instant } },
    });
    return Boolean(hit);
  }

  /**
   * The single figure the Overview tile shows.
   *
   * Deliberately derived from the CURRENT interval only, not from
   * `min(windowStart)` across all of them. If there is a gap, the honest answer
   * to "how far back can we go?" is "to the start of the unbroken stretch",
   * not "to the oldest byte we happen to still hold" — which would count
   * periods we cannot actually restore.
   */
  async advertisedWindow(targetId: string): Promise<{ start: Date; end: Date; hasGaps: boolean } | null> {
    const points = await this.prisma.recoveryPoint.findMany({
      where: { targetId },
      orderBy: { windowEnd: 'desc' },
    });
    if (!points.length) return null;

    const current = points.find((p) => p.isCurrent) ?? points[0];
    return {
      start: current.windowStart,
      end: current.windowEnd,
      hasGaps: points.some((p) => p.continuity === 'gap_before'),
    };
  }
}
